'use client';

import { Dialog, DialogPanel } from '@headlessui/react';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useMetrica } from 'next-yandex-metrica';
import { MouseEvent, useEffect, useState } from 'react';

import { useAuth } from '@/entities/user';
import { CareerOfferTrackedLink } from '@/features/career-offer-analytics';
import { PromoBar } from '@/features/promo-bar';
import {
	ChevronBottomIcon,
	ChevronRightIcon,
	HeaderMenuIcon,
	UserIcon,
	XMarkIcon,
} from '@/shared';
import { useLoginUrlWithRedirect } from '@/shared/hooks';
import { throttle, urlBuilder } from '@/shared/utils';

import { Button } from './';
import { trackCommunityClick } from './community-analytics';
import { Container } from './container';

const courseLinks = [
	{
		name: 'Алгоритмы и структуры данных',
		href: urlBuilder.course('algo-big-tech'),
	},
	{
		name: 'Сортировки: от Bubble до Quick Sort',
		href: urlBuilder.course('sort-algorithms'),
	},
	{
		name: 'Графы: с нуля до PRO',
		href: urlBuilder.course('graph-algorithms'),
	},
	{
		name: 'System Design для собеседований',
		href: urlBuilder.course('system-design'),
	},
	{
		name: 'Подготовка к Go-собеседованию',
		href: urlBuilder.course('go-interview'),
	},
];

export const Header = ({
	navLinks,
	actionButtonHref,
	actionButtonLabel,
	showCareerOfferLink = false,
	trackCommunityInteractions = false,
}: {
	navLinks: { href: string; label: string }[];
	actionButtonHref: string;
	actionButtonLabel: string;
	showCareerOfferLink?: boolean;
	trackCommunityInteractions?: boolean;
}) => {
	const { user } = useAuth();
	const metrica = useMetrica();
	const loginUrl = useLoginUrlWithRedirect();
	const [isOpen, setIsOpen] = useState(false);
	const [activeNav, setActiveNav] = useState(0);
	const trackMenuClick = (item: string, location: string) => {
		if (trackCommunityInteractions) {
			trackCommunityClick(metrica, 'menu', { item, location });
		}
	};
	const trackQuestionClick = (location: string) => {
		if (trackCommunityInteractions) {
			trackCommunityClick(metrica, 'question', { location });
		}
	};

	useEffect(() => {
		const [onScroll] = throttle(() => {
			const scrollY = window.scrollY + window.innerHeight / 2;

			let finedKey = 0;
			let minDistance = Infinity;

			navLinks.forEach((navLink, key) => {
				// только якорные ссылки (#section) можно использовать в querySelector
				if (!navLink.href.startsWith('#')) return;

				const section = document.querySelector(navLink.href);

				if (!section) {
					return;
				}

				const rect = section.getBoundingClientRect();
				const sectionTop = window.scrollY + rect.top;
				const sectionCenter = sectionTop + rect.height / 2;

				const distance = Math.abs(scrollY - sectionCenter);

				if (distance < minDistance) {
					minDistance = distance;
					finedKey = key;
				}
			});

			setActiveNav(finedKey);
		}, 50);

		window.addEventListener('scroll', onScroll);

		return () => {
			window.removeEventListener('scroll', onScroll);
		};
	}, [navLinks]);

	const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
		setIsOpen(false);

		const href = event.currentTarget.getAttribute('href');

		if (href?.startsWith('#')) {
			const targetBlock = document.querySelector(href);
			if (targetBlock) {
				event.preventDefault();
				const { top } = targetBlock.getBoundingClientRect();

				window.scrollTo({
					top: top + window.scrollY - 100,
					behavior: 'smooth',
				});
			}
		}
	};

	return (
		<header className="sticky top-0 z-50">
			<div className="relative z-10 w-full bg-[#F9F9F9]">
				<Container className="flex items-center gap-6 py-4 max-xl:justify-between max-xl:border-b max-xl:border-b-[#DBDFE9] max-xl:bg-white xl:px-10 2xl:justify-between 2xl:px-5">
					<button
						type="button"
						className="-ml-2 cursor-pointer p-2 text-base/7 text-[#000000] 2xl:hidden"
						onClick={() => {
							trackMenuClick('all_courses', 'mobile_header');
							setIsOpen(true);
						}}
					>
						{isOpen ? (
							<XMarkIcon className="size-6" />
						) : (
							<HeaderMenuIcon className="size-6" />
						)}
					</button>
					<Image
						src="/logo-algocode.svg"
						alt="algocode.io"
						width={397}
						height={59}
						className="h-7 w-auto xl:h-10 2xl:w-[271px] 2xl:object-contain 2xl:object-left"
					/>
					<nav className="mx-auto hidden 2xl:flex">
						<Link
							href={'#'}
							className="flex cursor-pointer flex-row items-center gap-2 text-nowrap px-6 py-3 text-[#4B5675] hover:text-[#090E1C]"
							onClick={() => {
								trackMenuClick('all_courses', 'desktop_header');
								setIsOpen(true);
							}}
						>
							Все курсы <ChevronBottomIcon className="size-5" />
						</Link>
						{navLinks.map((link, key) => {
							const className = clsx('cursor-pointer text-nowrap px-6 py-3', {
								'text-[#4B5675] hover:text-[#090E1C]': activeNav !== key,
								'text-[#090E1C]': activeNav === key,
							});
							return (
								<Link
									key={link.href}
									href={link.href}
									className={className}
									onClick={(event) => {
										trackMenuClick(link.href, 'desktop_header');
										onClick(event);
									}}
								>
									{link.label}
								</Link>
							);
						})}
					</nav>

					<div className="flex flex-row items-center gap-5 xl:ml-auto 2xl:ml-0">
						{!user && (
							<Link
								href={loginUrl}
								className="flex cursor-pointer flex-row items-center gap-4 text-nowrap py-3 text-[#4B5675] hover:text-[#090E1C]"
								onClick={() => trackMenuClick('login', 'header')}
							>
								<UserIcon className="size-6 xl:hidden" />
								<span className="max-xl:hidden">Войти</span>
							</Link>
						)}
						<Button
							href={actionButtonHref}
							size="48"
							variant="primary"
							onClick={(event: MouseEvent<HTMLAnchorElement>) => {
								trackQuestionClick('header');
								onClick(event);
							}}
						>
							<span className="max-xl:hidden">{actionButtonLabel}</span>
							<ChevronRightIcon className="size-5" />
						</Button>
					</div>
				</Container>
			</div>

			<Dialog open={isOpen} onClose={setIsOpen}>
				<DialogPanel className="fixed left-0 right-0 top-[73px] z-50 flex flex-col justify-between overflow-y-auto bg-white p-4 pb-12 max-lg:bottom-0 lg:mx-5 lg:rounded-[32px] lg:py-3 lg:shadow-md xl:top-20 2xl:left-1/2 2xl:right-auto 2xl:ml-20 2xl:-translate-x-full">
					<nav className="flex flex-col">
						{navLinks.map((link, key) => {
							const className = clsx(
								'cursor-pointer rounded-2xl px-6 py-3 2xl:hidden',
								{
									'text-[#4B5675] hover:text-[#090E1C]': activeNav !== key,
									'bg-[#F9F9F9] text-[#090E1C]': activeNav === key,
								},
							);
							return (
								<Link
									key={link.href}
									href={link.href}
									className={className}
									onClick={(event) => {
										trackMenuClick(link.href, 'mobile_menu');
										onClick(event);
									}}
								>
									{link.label}
								</Link>
							);
						})}
						<div className="my-3 h-px bg-[#DBDFE9] 2xl:hidden" />
						{showCareerOfferLink && (
							<>
								<div className="px-6 py-3 text-gray-3 2xl:hidden">
									Программы
								</div>
								<CareerOfferTrackedLink
									placement="community_courses_menu"
									entrypoint="menu"
									className="cursor-pointer rounded-2xl px-6 py-3 text-[#4B5675] hover:text-[#090E1C] 2xl:hidden"
									onClick={(event) => {
										trackMenuClick('/career-offer', 'mobile_menu');
										onClick(event);
									}}
								>
									Программа до оффера
								</CareerOfferTrackedLink>
								<div className="my-3 h-px bg-[#DBDFE9] 2xl:hidden" />
							</>
						)}
						<div className="px-6 py-3 text-gray-3 2xl:hidden">
							Курсы сообщества
						</div>
						{showCareerOfferLink && (
							<CareerOfferTrackedLink
								placement="community_courses_menu"
								entrypoint="menu"
								className="hidden cursor-pointer rounded-2xl px-6 py-3 text-[#4B5675] hover:text-[#090E1C] 2xl:block"
								onClick={(event) => {
									trackMenuClick('/career-offer', 'desktop_menu');
									onClick(event);
								}}
							>
								Программа до оффера
							</CareerOfferTrackedLink>
						)}
						{courseLinks.map((link) => (
							<Link
								key={link.href}
								href={link.href}
								className="cursor-pointer rounded-2xl px-6 py-3 text-[#4B5675] hover:text-[#090E1C]"
								onClick={(event) => {
									trackMenuClick(link.href, 'courses_menu');
									onClick(event);
								}}
							>
								{link.name}
							</Link>
						))}
					</nav>
					<Button
						href={actionButtonHref}
						size="72"
						variant="primary"
						className="lg:hidden"
						shadow
						onClick={(event: MouseEvent<HTMLAnchorElement>) => {
							trackQuestionClick('mobile_menu');
							onClick(event);
						}}
					>
						{actionButtonLabel}
						<ChevronRightIcon className="size-5" />
					</Button>
				</DialogPanel>
			</Dialog>
			<div className="absolute left-0 right-0 top-full z-0 w-full">
				<PromoBar />
			</div>
		</header>
	);
};
